import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getSemanticPageIdentifier } from '@/helpers/aiChat/agentConfig';
import {
  getQuickPromptsForPage,
  MAX_QUICK_PROMPTS,
  pickRandomSubset,
  type QuickPrompt,
} from '@/constants/quickPrompts';

type UseQuickPromptsForPageOptions = {
  /**
   * Pass true when the empty state is visible (drawer open with no messages, or full page with no messages).
   * When this transitions to true we pick a new random 4 so: drawer close/open shows new prompts,
   * and after "clear conversation" quick prompts show again with a fresh set.
   */
  isShowingEmptyState?: boolean;
  /**
   * When provided and non-empty, these prompts are returned directly instead
   * of the page-specific ones. Used to surface GS-specific prompts on the dashboard.
   */
  overridePrompts?: QuickPrompt[];
};

/**
 * Returns up to 4 quick prompts for the current page.
 * Uses the current route path to resolve the semantic page identifier.
 * When the page has more than 4 prompts, returns a random 4. Re-randomizes when
 * isShowingEmptyState transitions to true (drawer opened again or conversation cleared).
 */
export function useQuickPromptsForPage(
  options: UseQuickPromptsForPageOptions = {}
): QuickPrompt[] {
  const { isShowingEmptyState = false, overridePrompts } = options;
  const { t, i18n } = useTranslation('ai-chat');
  const location = useLocation();
  let pageId = getSemanticPageIdentifier(location.pathname);
  // Settings uses tab query param; use tab-specific page id when on Company Profile or Device Management
  if (location.pathname === '/settings') {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'company') {
      pageId = 'settings_company_profile';
    } else if (tab === 'device') {
      pageId = 'settings_device';
    } else if (tab === 'billing') {
      pageId = 'settings_billing';
    } else if (tab === 'support-center') {
      pageId = 'settings_support_center';
    } else if (tab === 'integrations') {
      pageId = 'settings_integrations';
    }
  }
  const prevShowingRef = useRef(isShowingEmptyState);
  const [generation, setGeneration] = useState(0);

  // When we transition to "showing empty" (drawer open or after clear), increment so we get new random 4
  useEffect(() => {
    const wasShowing = prevShowingRef.current;
    prevShowingRef.current = isShowingEmptyState;
    if (!wasShowing && isShowingEmptyState) {
      setGeneration(g => g + 1);
    }
  }, [isShowingEmptyState]);

  // Re-run when page or when we re-enter empty state (generation bumps) to get new random 4
  return useMemo(() => {
    if (overridePrompts && overridePrompts.length > 0) {
      return overridePrompts;
    }
    return pickRandomSubset(
      getQuickPromptsForPage(pageId, t),
      MAX_QUICK_PROMPTS
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- generation is intentional to re-randomize when empty state is shown again
  }, [pageId, generation, overridePrompts, t, i18n.language]);
}
